// Utilitários do módulo de Clientes: domínio, validação, máscaras, filtros e payload.
// Os valores dos enums e as regras de CPF/CNPJ espelham o backend (gestao_clientes).

/* ------------------------------------------------------------------ */
/* Domínio                                                             */
/* ------------------------------------------------------------------ */

export const TIPOS_PESSOA = [
    { value: "F", label: "Pessoa Física" },
    { value: "J", label: "Pessoa Jurídica" },
];

export const SEXOS = [
    { value: "M", label: "Masculino" },
    { value: "F", label: "Feminino" },
    { value: "O", label: "Outro" },
];

export const ESTADOS_CIVIS = [
    { value: "solteiro", label: "Solteiro(a)" },
    { value: "casado", label: "Casado(a)" },
    { value: "uniao_estavel", label: "União estável" },
    { value: "separado", label: "Separado(a)" },
    { value: "divorciado", label: "Divorciado(a)" },
    { value: "viuvo", label: "Viúvo(a)" },
];

export const GRAUS_PARENTESCO = [
    { value: "conjuge", label: "Cônjuge" },
    { value: "companheiro", label: "Companheiro(a)" },
    { value: "filho", label: "Filho(a)" },
    { value: "pai", label: "Pai" },
    { value: "mae", label: "Mãe" },
    { value: "irmao", label: "Irmão(ã)" },
    { value: "neto", label: "Neto(a)" },
    { value: "avo", label: "Avô(ó)" },
    { value: "outro", label: "Outro" },
];

export const UFS = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA",
    "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
].map(uf => ({ value: uf, label: uf }));

/** Rótulos dos campos que mudam de significado conforme o tipo de pessoa. */
export const rotulosPorTipo = (tipo) =>
    tipo === "J"
        ? {
            nome: "Razão social",
            documento: "CNPJ",
            registro: "Inscrição estadual",
            data: "Data de fundação",
            atividade: "Ramo de atividade",
        }
        : {
            nome: "Nome completo",
            documento: "CPF",
            registro: "RG",
            data: "Data de nascimento",
            atividade: "Profissão",
        };

/* ------------------------------------------------------------------ */
/* Normalização e validação de documentos                              */
/* ------------------------------------------------------------------ */

export const somenteDigitos = (v) => String(v ?? "").replace(/\D/g, "");

/**
 * Remove pontuação do documento. CPF: só dígitos (11). CNPJ: dígitos e letras
 * maiúsculas (14), pois o CNPJ alfanumérico vale a partir de julho de 2026.
 */
export const normalizarDocumento = (v, tipo) =>
    tipo === "J"
        ? String(v ?? "").replace(/[^0-9a-zA-Z]/g, "").toUpperCase().slice(0, 14)
        : somenteDigitos(v).slice(0, 11);

export function cpfValido(cpf) {
    if (!/^\d{11}$/.test(cpf ?? "") || cpf === cpf[0].repeat(11)) return false;
    const dv = (n) => {
        let soma = 0;
        for (let i = 0; i < n; i++) soma += Number(cpf[i]) * (n + 1 - i);
        return ((soma * 10) % 11) % 10;
    };
    return dv(9) === Number(cpf[9]) && dv(10) === Number(cpf[10]);
}

export function cnpjValido(cnpj) {
    if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj ?? "") || cnpj === cnpj[0].repeat(14)) {
        return false;
    }
    const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const pesos2 = [6, ...pesos1];
    // Valor de cada caractere = código ASCII - 48 ('0'..'9' => 0..9, 'A' => 17, ...)
    const dv = (base, pesos) => {
        const soma = base
            .split("")
            .reduce((s, c, i) => s + (c.charCodeAt(0) - 48) * pesos[i], 0);
        const resto = soma % 11;
        return resto < 2 ? 0 : 11 - resto;
    };
    const base = cnpj.slice(0, 12);
    const d1 = dv(base, pesos1);
    const d2 = dv(base + d1, pesos2);
    return cnpj.slice(12) === `${d1}${d2}`;
}

/* ------------------------------------------------------------------ */
/* Máscaras de exibição                                                */
/* ------------------------------------------------------------------ */

export function formatarCpf(v) {
    return somenteDigitos(v)
        .slice(0, 11)
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

export function formatarCnpj(v) {
    const c = normalizarDocumento(v, "J");
    let out = c.slice(0, 2);
    if (c.length > 2) out += "." + c.slice(2, 5);
    if (c.length > 5) out += "." + c.slice(5, 8);
    if (c.length > 8) out += "/" + c.slice(8, 12);
    if (c.length > 12) out += "-" + c.slice(12, 14);
    return out;
}

export const formatarDocumento = (v, tipo) =>
    tipo === "J" ? formatarCnpj(v) : formatarCpf(v);

/** Para listagens: infere CPF/CNPJ pelo tamanho do valor armazenado. */
export const formatarDocumentoAuto = (v) =>
    String(v ?? "").length === 14 ? formatarCnpj(v) : formatarCpf(v);

export function formatarTelefone(v) {
    const d = somenteDigitos(v).slice(0, 11);
    if (!d) return "";
    if (d.length <= 2) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export function formatarCep(v) {
    const d = somenteDigitos(v).slice(0, 8);
    return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

/** "2026-10-20", "2026-10-20 18:30:00" ou ISO com "T" -> "20/10/2026" (com hora opcional). */
export function formatarData(valor, showTime = false) {
    if (!valor) return "";
    const [data, hora] = String(valor).replace("T", " ").split(" ");
    const [ano, mes, dia] = data.split("-");
    if (!ano || !mes || !dia) return String(valor);
    const base = `${dia.slice(0, 2)}/${mes}/${ano}`;
    return showTime && hora ? `${base} ${hora.slice(0, 5)}` : base;
}

/** Data local de hoje em AAAA-MM-DD (para comparar com <input type="date">). */
export const hojeISO = () => new Date().toLocaleDateString("sv-SE");

/* ------------------------------------------------------------------ */
/* Beneficiários                                                       */
/* ------------------------------------------------------------------ */

/** Usa a contagem vinda da listagem ou, se ausente, o tamanho da lista aninhada. */
export const totalBeneficiarios = (cliente) =>
    Number(cliente?.total_beneficiarios ?? cliente?.beneficiarios?.length ?? 0);

/** Soma dos percentuais, calculada em centésimos para evitar erro de ponto flutuante. */
export function somaPercentuais(beneficiarios) {
    const centesimos = (beneficiarios ?? []).reduce(
        (soma, b) => soma + Math.round((Number(b.percentual_participacao) || 0) * 100),
        0
    );
    return centesimos / 100;
}

/* ------------------------------------------------------------------ */
/* Filtros da listagem                                                 */
/* ------------------------------------------------------------------ */

export const FILTROS_INICIAIS = {
    busca: "",
    tipo_pessoa: "",
    status: "",
    beneficiarios: "",
};

export function filtrarClientes(clientes, filtros) {
    const termo = (filtros.busca ?? "").trim().toLowerCase();
    const termoDoc = (filtros.busca ?? "").replace(/[^0-9a-zA-Z]/g, "").toUpperCase();

    return clientes.filter((c) => {
        if (termo) {
            const nome = (c.nome_razao_social ?? "").toLowerCase();
            const email = (c.email ?? "").toLowerCase();
            const doc = (c.cpf_cnpj ?? "").toUpperCase();
            const bate = nome.includes(termo) || email.includes(termo) || (termoDoc && doc.includes(termoDoc));
            if (!bate) return false;
        }

        if (filtros.tipo_pessoa && c.tipo_pessoa !== filtros.tipo_pessoa) return false;

        if (filtros.status === "ativo" && c.ativo === false) return false;
        if (filtros.status === "inativo" && c.ativo !== false) return false;

        const total = totalBeneficiarios(c);
        if (filtros.beneficiarios === "com" && total === 0) return false;
        if (filtros.beneficiarios === "sem" && total > 0) return false;

        return true;
    });
}

/* ------------------------------------------------------------------ */
/* Payload de gravação                                                 */
/* ------------------------------------------------------------------ */

const vazioParaNulo = (v) => (v === "" || v === undefined ? null : v);

/**
 * Converte os valores do formulário no corpo enviado à API:
 * strings vazias viram null, sexo/estado civil são zerados para PJ e os
 * beneficiários seguem aninhados (a lista enviada substitui a existente).
 */
export function montarPayload(values, cliente) {
    const { beneficiarios, ...dados } = values;

    const payload = {};
    Object.entries(dados).forEach(([chave, valor]) => {
        payload[chave] = vazioParaNulo(typeof valor === "string" ? valor.trim() : valor);
    });

    if (payload.tipo_pessoa === "J") {
        payload.sexo = null;
        payload.estado_civil = null;
    }
    if (payload.email) payload.email = payload.email.toLowerCase();
    if (payload.estado) payload.estado = payload.estado.toUpperCase();

    payload.beneficiarios = (beneficiarios ?? []).map((b) => ({
        ...(b.id ? { id: b.id } : {}),
        nome: b.nome.trim(),
        cpf: vazioParaNulo(b.cpf),
        data_nascimento: vazioParaNulo(b.data_nascimento),
        grau_parentesco: b.grau_parentesco,
        percentual_participacao: Number(b.percentual_participacao),
    }));

    if (cliente?.id) payload.id = cliente.id;
    return payload;
}
